/**
 * BookingSummary - Ride options display and selection
 *
 * i18n (Phase 5):
 *   - Uses booking.* (searchingOptions, chooseRide, fixedFareGuaranteed,
 *     estimatedPrice) and rideOptions.* (surge) namespaces.
 *   - Receives `t` as a prop from the BookingWidget orchestrator.
 *   - option.service_name, option.vehicle_example and option.features come
 *     straight from the API response and are translated server-side.
 *   - Numeric values (km, min, EUR amount, surge multiplier) are kept as
 *     literal text.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Leaf, Crown, Car, Check, Loader2, Users } from 'lucide-react';

// Tier visual identity — must match BookingForm.jsx so Step 1 and Step 2
// look like the same picker. Only the ring (selection) and icon palette
// carry brand colour; the card chrome is white + neutral border.
const tierStyles = {
  eco:      { Icon: Leaf,  text: 'text-green-600',   ring: 'ring-green-500',   chipBg: 'bg-green-100' },
  van:      { Icon: Users, text: 'text-blue-600',    ring: 'ring-blue-500',    chipBg: 'bg-blue-100' },
  business: { Icon: Car,   text: 'text-neutral-900', ring: 'ring-neutral-900', chipBg: 'bg-neutral-100' },
  // G3.E: PRIORITY removed from customer-widget tier vocabulary.
};

export const BookingSummary = ({
  loadingOptions,
  rideOptions,
  selectedOption,
  estimate,
  onSelectOption,
  t
}) => {
  if (loadingOptions) {
    return (
      <div className="bg-neutral-50 border border-neutral-200 p-8 rounded-xl text-center">
        <Loader2 className="w-8 h-8 text-black mx-auto animate-spin mb-3" />
        <p className="text-neutral-500">{t('booking.searchingOptions')}</p>
      </div>
    );
  }

  if (rideOptions && rideOptions.options) {
    // Same explicit order as Step 1's inline picker so the visual flow stays
    // consistent across steps. G3.E: PRIORITY removed from customer-widget
    // tier vocabulary; 3-card layout (eco / business / van).
    const order = ['eco', 'business', 'van'];
    const optionsByType = Object.fromEntries(rideOptions.options.map(o => [o.service_type, o]));
    const orderedOptions = order.map(t => optionsByType[t]).filter(Boolean);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-3"
      >
        <div className="text-center mb-2">
          <h3 className="text-lg font-heading font-semibold text-black mb-1">
            {t('booking.chooseRide')}
          </h3>
          <p className="text-sm text-neutral-500">
            {rideOptions.route_info.distance_km} km · ~{rideOptions.route_info.duration_minutes} min
          </p>
          {rideOptions.is_fixed_fare && (
            <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-600 text-xs rounded-full font-medium">
              {t('booking.fixedFareGuaranteed')}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {orderedOptions.map((option) => {
            const style = tierStyles[option.service_type] || tierStyles.business;
            const Icon = style.Icon;
            const isSelected = selectedOption?.service_type === option.service_type;
            return (
              <button
                type="button"
                key={option.service_type}
                onClick={() => onSelectOption(option)}
                className={`text-left p-3 rounded-xl border bg-white transition-colors ${
                  isSelected
                    ? `border-transparent ring-2 ${style.ring} shadow-sm`
                    : 'border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300'
                }`}
                data-testid={`ride-option-${option.service_type}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.chipBg}`}>
                    <Icon className={`w-4 h-4 ${style.text}`} />
                  </div>
                  <span className={`font-heading font-semibold text-sm ${style.text} truncate flex-1`}>
                    {option.service_name}
                  </span>
                  {isSelected && <Check className={`w-4 h-4 flex-shrink-0 ${style.text}`} />}
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs text-neutral-500 truncate">
                    {option.estimated_pickup_minutes != null ? `~${option.estimated_pickup_minutes} min` : ''}
                  </span>
                  <span className="text-base font-bold text-black whitespace-nowrap">
                    {option.estimated_price}€
                  </span>
                </div>
                {option.is_surge_applied && (
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-orange-500">
                    <Zap className="w-3 h-3" />
                    <span>{t('rideOptions.surge')} x{option.surge_multiplier}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </motion.div>
    );
  }

  // Fallback single estimate
  if (estimate) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-neutral-50 border border-neutral-200 p-5 rounded-xl mb-4"
      >
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-black font-semibold mb-1">
            {t('booking.estimatedPrice')}
          </p>
          <p className="text-4xl font-heading font-bold text-black" data-testid="estimated-price">
            {estimate.estimated_price} €
          </p>
          <p className="text-sm text-neutral-500 mt-2">
            {estimate.distance_km} km · ~{estimate.duration_minutes} min
          </p>
          {estimate.is_fixed_fare && (
            <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-600 text-xs rounded-full font-medium">
              {t('booking.fixedFareGuaranteed')}
            </span>
          )}
        </div>
      </motion.div>
    );
  }

  return null;
};

export default BookingSummary;
